// Angular
import { inject, Injectable } from '@angular/core';

// Data Contracts
import { ClerkReviewQueryRequest } from '@data-contracts';

// Local
import { IdentityClaimsService } from '@app/services/identity-claims/identity-claims.service';
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { ClerkReviewFilterSelectionService } from '@feature/crl/clerk-review-filter-selection.service';
import { IClerkReviewFilterSelection } from '@feature/crl/clerk-review-filter-selection.service';

@Injectable({
  providedIn: 'root',
})
export class ClerkReviewFilterStoreValuesService {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly payloadService = inject(ClerkReviewFilterPayloadService);
  private readonly otherPayloadService = inject(
    ClerkReviewFilterSelectionService,
  );
  private readonly identityClaimsService = inject(IdentityClaimsService);

  filtersLocalStoreKey = 'clerkReviewFilterValues';
  otherFiltersLocalStoreKey = 'clerkReviewOtherFilterValues';

  constructor() {
    this.filtersLocalStoreKey = `clerkReviewFilterValues_${this.identityClaimsService.userId()}`;
    this.otherFiltersLocalStoreKey = `clerkReviewOtherFilterValues_${this.identityClaimsService.userId()}`;
  }

  /**
   * Set up listeners for the filter payload and other filter selections.
   * This will need to be called in the onInit of the component that uses the filters.
   *
   * @param queueIdFromQuery A `queueId` query param from the clerk dashboard
   *   "My Pending Work" queue links. When present it overrides the stored queue
   *   and resets paging so the first list load matches the clicked queue.
   */
  startFilterListener(queueIdFromQuery?: string | null): void {
    //first we want to take local storage items and pre-fill the filter selection object
    const storedValues = this.getLocalStorage();
    if (storedValues) {
      this.payloadService.setFilterPayload(storedValues);
    }
    if (queueIdFromQuery) {
      this.payloadService.setQueueFilter(queueIdFromQuery);
      this.payloadService.setSkip(0);
    }
    this.payloadService.filterPayload$.subscribe(payload => {
      this.updateLocalStorage(payload);
    });
  }

  /**
   * Forgets the stored filters and clears the in-memory payload and other-filter
   * selections (both services are root singletons, so a previous visit's values
   * would otherwise survive), so the list next opens with no filters active, as
   * after Reset. The clerk dashboard "Pending" link calls this as it navigates to
   * the list, so nothing in the URL replays the reset on Back or refresh.
   */
  clearStoredFilters(): void {
    localStorage.removeItem(this.filtersLocalStoreKey);
    localStorage.removeItem(this.otherFiltersLocalStoreKey);
    this.payloadService.resetPayload();
    this.otherPayloadService.clearFilterSelections();
  }

  startOtherFilterListener(): void {
    this.otherPayloadService.clerkReviewFilterSelections$.subscribe(payload => {
      this.updateOtherLocalStorage(payload);
    });
  }

  /**
   *
   * @param payload The payload to store in local storage.
   */
  updateLocalStorage(payload: ClerkReviewQueryRequest): void {
    localStorage.setItem(this.filtersLocalStoreKey, JSON.stringify(payload));
  }
  updateOtherLocalStorage(payload: IClerkReviewFilterSelection[]): void {
    localStorage.setItem(
      this.otherFiltersLocalStoreKey,
      JSON.stringify(payload),
    );
  }

  getLocalStorage(): ClerkReviewQueryRequest {
    const storedValues = localStorage.getItem(this.filtersLocalStoreKey);
    return storedValues ? JSON.parse(storedValues) : null;
  }
  getOtherLocalStorage(): IClerkReviewFilterSelection[] {
    const storedValues = localStorage.getItem(this.otherFiltersLocalStoreKey);
    return storedValues ? JSON.parse(storedValues) : null;
  }

  /*
   * Get the stored value for the court filter.
   */
  getStoredCourtValue(): number | null {
    const filters = this.getLocalStorage();
    return filters?.courtId?.value;
  }

  /*
   * Get the stored value for the queue filter.
   */
  getStoredQueueValue(): string | null {
    const filters = this.getLocalStorage();
    return filters?.queueIds?.value[0];
  }

  /*
   * Get the stored value for the judge filter.
   */
  getStoredJudgeValue(): number | null {
    const filters = this.getLocalStorage();
    return filters?.judgeId?.value;
  }

  /*
   * Get the stored value for the document filter.
   */
  getStoredDocumentValue(): boolean | null {
    const filters = this.getLocalStorage();
    return filters?.searchSupportingDocuments?.value;
  }

  /*
   * Get the stored value for the date range filter.
   */
  getStoredDateRangeValue(): { start: Date | null; end: Date | null } | null {
    const filters = this.getLocalStorage();
    return {
      start: new Date(filters?.dateRange?.value?.startDate),
      end: new Date(filters?.dateRange?.value?.endDate),
    };
  }

  getStoredPerPageValue(): number | null {
    const filters = this.getLocalStorage();
    return filters?.take;
  }

  getStoredPageValue(): number | null {
    const filters = this.getLocalStorage();
    return filters?.skip;
  }
}
